/**
 * Classify template fields for layout rules: identifiers never wrap;
 * content fields (names, addresses) may wrap across lines.
 */

import { normalizeHeader } from "./columnMatcher";

export type FieldKind = "identifier" | "content" | "default";

/** Normalized names treated as codes / barcodes / order ids — no auto wrap. */
const IDENTIFIER_NAMES = new Set([
  "mahang",
  "masp",
  "sku",
  "masanpham",
  "itemcode",
  "sap",
  "mavach",
  "gtin",
  "barcode",
  "upc",
  "ean",
  "mabarcodehanghoa",
  "mabarcode",
  "sopo",
  "madon",
  "madonhang",
  "po",
  "ponumber",
  "maphieu",
  "sochungtu",
  "sodondathang",
  "masodondathang",
  "mach",
  "macuahang",
  "madimgiao",
  "madiemgiao",
  "mancc",
  "manhacungcap",
  "suppliercode",
  "manhacc",
]);

/** Normalized names that are multi-line prose (product name, address). */
const CONTENT_NAMES = new Set([
  "tenhang",
  "tensanpham",
  "tensp",
  "diengiai",
  "itemname",
  "productname",
  "mathang",
  "diachi",
  "diadiem",
  "diachigiaohang",
  "diachidiemgiao",
  "diachincc",
  "diachinhanhang",
  "ghichu",
  "ghichuhang",
  "note",
  "remark",
]);

/**
 * Heuristic: labels containing mã / code / barcode / PO-like tokens are identifiers
 * unless they clearly look like a name/address field.
 */
export function classifyFieldKind(fieldName: string): FieldKind {
  const norm = normalizeHeader(fieldName);
  if (!norm) return "default";
  if (IDENTIFIER_NAMES.has(norm)) return "identifier";
  if (CONTENT_NAMES.has(norm)) return "content";

  // Partial token checks for labels like "Mã số đơn đặt hàng", "Mã Barcode hàng hóa"
  const looksId =
    /(^|[^a-z])(ma|code|barcode|gtin|sku|po|sopo)([^a-z]|$)/.test(norm) ||
    norm.includes("mavach") ||
    norm.includes("barcode") ||
    norm.includes("madon");
  const looksContent =
    norm.includes("ten") ||
    norm.includes("diachi") ||
    norm.includes("diengiai") ||
    norm.includes("ghichu") ||
    norm.includes("address");

  if (looksId && !looksContent) return "identifier";
  if (looksContent && !looksId) return "content";
  return "default";
}

export function isIdentifierField(fieldName: string): boolean {
  return classifyFieldKind(fieldName) === "identifier";
}

export function isContentField(fieldName: string): boolean {
  return classifyFieldKind(fieldName) === "content";
}
